import React from 'react';
import './ErrorBoundary.scss';
import { reportError } from '../utils/errorReporting';

type Props = {
  children: React.ReactNode;
  /** Names the guarded area in the error report. */
  name?: string;
  /**
   * When provided (null is valid), rendered in place of the full-page error
   * screen, so a failure in a non-essential area leaves the rest of the app
   * running.
   */
  fallback?: React.ReactNode;
  /** Runs after the error is reported, e.g. to close the modal that failed. */
  onError?: () => void;
};

type State = {
  hasError: boolean;
};

// JAA-388: with no boundary anywhere in the tree, an uncaught error thrown
// from a render or an effect (e.g. a SecurityError from a blocked
// localStorage/sessionStorage call) unmounts the whole app into a blank
// white page with no feedback. This boundary catches that and shows a
// recoverable error screen instead. Kept free of i18n/contexts so it still
// renders if the crash happened before those initialized.
class ErrorBoundary extends React.Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError(): State {
    return { hasError: true };
  }

  componentDidCatch(error: Error, info: React.ErrorInfo): void {
    // eslint-disable-next-line no-console
    reportError(error, {
      boundary: this.props.name ?? 'app',
      componentStack: info.componentStack ?? '',
    });
    this.props.onError?.();
  }

  handleReload = (): void => {
    window.location.reload();
  };

  render(): React.ReactNode {
    if (!this.state.hasError) {
      return this.props.children;
    }

    if (this.props.fallback !== undefined) {
      return this.props.fallback;
    }

    const isFinnish = (document.documentElement.lang || navigator.language || '').toLowerCase().startsWith('fi');
    const title = isFinnish ? 'Hups! Jotain meni pieleen.' : 'Oops! Something went wrong.';
    const message = isFinnish
      ? 'Sivun lataaminen epäonnistui. Yritä päivittää sivu uudelleen.'
      : 'The page failed to load. Please try refreshing.';
    const retry = isFinnish ? 'Päivitä sivu' : 'Refresh page';

    return (
      <div className="error-boundary-fallback">
        <div className="error-boundary-fallback__content">
          <div className="error-boundary-fallback__icon">
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="40"
              height="40"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <circle cx="12" cy="12" r="10" />
              <line x1="12" y1="8" x2="12" y2="12" />
              <line x1="12" y1="16" x2="12.01" y2="16" />
            </svg>
          </div>
          <div className="error-boundary-fallback__title">{title}</div>
          <div className="error-boundary-fallback__message">{message}</div>
          <button className="error-boundary-fallback__retry-button" onClick={this.handleReload}>
            {retry}
          </button>
        </div>
      </div>
    );
  }
}

export default ErrorBoundary;
